import { Collapse, Divider, Stack, Typography } from '@mui/material';
import type { ApprovalFlowInvoiceTypes, GetApprovalFlowInvoicesListResponse } from '@vy/api-definition';
import {
  caseInsensitiveIncludes,
  groupBy,
  isEmptyString,
  isNotEmptyArray,
  isNotNullOrUndefined,
  vyDate,
} from '@vy/utils';
import React, { useEffect, useMemo, useState } from 'react';
import type { Size } from 'react-virtualized-auto-sizer';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { VyLoader } from '../../../../components/new-design/feedback/Loader/VyLoader.tsx';
import { ApprovalFlowInvoicesListHeader } from './ApprovalFlowInvoicesListHeader';
import { ApprovalFlowInvoicesListItem } from './ApprovalFlowInvoicesListItem';

type ApprovalFlowInvoicesListProps = {
  invoicesData?: GetApprovalFlowInvoicesListResponse;
  selectedInvoiceId?: string;
  onInvoiceClick?: (invoiceId: string) => void;
  onFiltersChange?: (filters: ApprovalFlowInvoiceTypes[]) => void;
};
export const LINE_ITEM_SIZE = 52;
export const ApprovalFlowInvoicesList: React.FC<ApprovalFlowInvoicesListProps> = ({
  onFiltersChange,
  invoicesData,
  selectedInvoiceId,
  onInvoiceClick,
}) => {
  const fixedSizeListRef = React.useRef<VariableSizeList>(null);
  const [search, setSearch] = useState<string>('');

  const filteredInvoices = useMemo(
    () =>
      invoicesData?.filter(
        (invoice) =>
          isEmptyString(search) ||
          caseInsensitiveIncludes(invoice.invoiceName, search) ||
          caseInsensitiveIncludes(invoice.invoiceId, search) ||
          caseInsensitiveIncludes(invoice.customerName, search) ||
          (isNotNullOrUndefined(invoice.contractName) &&
            caseInsensitiveIncludes(invoice.contractName, search)),
      ) ?? [],
    [invoicesData, search],
  );

  const invoicesByMonthYearLabel = useMemo(
    () =>
      groupBy(filteredInvoices ?? [], (invoice) => {
        const invoiceEndDate = vyDate(invoice.invoicePeriod.startTime);

        return invoiceEndDate.format('MMMM YYYY');
      }),
    [filteredInvoices],
  );

  const handleInvoiceClick = (invoiceId: string) => {
    onInvoiceClick?.(invoiceId);
  };

  useEffect(() => {
    if (isNotNullOrUndefined(selectedInvoiceId)) {
      fixedSizeListRef.current?.scrollToItem(
        filteredInvoices.findIndex((invoice) => invoice.invoiceId === selectedInvoiceId),
      );
    }
  }, [filteredInvoices, selectedInvoiceId]);

  const getInvoiceDetails = (index: number) => {
    const currentInvoice = filteredInvoices[index];
    const invoiceLabelKey = vyDate(currentInvoice.invoicePeriod.startTime).format('MMMM YYYY');
    const [firstInvoiceInGroup] = invoicesByMonthYearLabel.get(invoiceLabelKey) ?? [undefined];

    return { currentInvoice, invoiceLabelKey, firstInvoiceInGroup };
  };

  return (
    <Stack direction="row" sx={{
      height: "100%"
    }}>
      <Collapse
        in={true}
        orientation="horizontal"
        sx={{
          height: '100%',
        }}
      >
        <Stack
          sx={(theme) => ({
            width: '354px',
            minWidth: '354px',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
          })}
        >
          <ApprovalFlowInvoicesListHeader onSearchChange={setSearch} onFiltersChange={onFiltersChange} />
          <Stack
            direction="column"
            sx={{
              height: "100%",
              gap: 0.5,
              overflowY: 'auto'
            }}>
            {isNotEmptyArray(invoicesData) ? (
              <AutoSizer>
                {({ height, width }: Size) => (
                  <VariableSizeList
                    ref={fixedSizeListRef}
                    height={height}
                    initialScrollOffset={
                      filteredInvoices.findIndex((invoice) => invoice.invoiceId === selectedInvoiceId) *
                      LINE_ITEM_SIZE
                    }
                    width={width}
                    itemCount={filteredInvoices.length}
                    itemSize={(index) => {
                      const { currentInvoice, firstInvoiceInGroup } = getInvoiceDetails(index);

                      return firstInvoiceInGroup?.invoiceId === currentInvoice.invoiceId
                        ? LINE_ITEM_SIZE + 14
                        : LINE_ITEM_SIZE;
                    }}
                  >
                    {({ index, style }) => {
                      const { currentInvoice, invoiceLabelKey, firstInvoiceInGroup } =
                        getInvoiceDetails(index);

                      return (
                        <div style={style} key={currentInvoice.invoiceId}>
                          {firstInvoiceInGroup?.invoiceId === currentInvoice.invoiceId && (
                            <Divider flexItem orientation="horizontal">
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "12px",
                                  lineHeight: "14px",
                                  color: "text.secondary"
                                }}>
                                {invoiceLabelKey}
                              </Typography>
                            </Divider>
                          )}
                          <ApprovalFlowInvoicesListItem
                            invoice={currentInvoice}
                            isSelected={selectedInvoiceId === currentInvoice.invoiceId}
                            onClick={handleInvoiceClick}
                          />
                        </div>
                      );
                    }}
                  </VariableSizeList>
                )}
              </AutoSizer>
            ) : (
              <Stack
                spacing={2}
                sx={{
                  padding: "8px",
                  paddingLeft: "16px",
                  paddingRight: "16px"
                }}>
                <VyLoader height="52px" />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};
